'use client';

import React, { useCallback, useState } from 'react';
import AppLogo from '@/core/presentation/components/ui/AppLogo';
import { LegalDocumentViewerModal } from '@/core/presentation/components/LegalDocumentViewerModal';
import { APP_BUILD_VERSION } from '@/core/presentation/constants/app-build-version';
import {
  LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_PRIVACY,
  LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_TERMS,
} from '@/core/presentation/constants/legal-document-viewer-copy';
import {
  FOOTER_LEGAL_DEVELOPER_CREDIT,
  FOOTER_LEGAL_LINK_PRIVACY,
  FOOTER_LEGAL_LINK_TERMS,
  FOOTER_LEGAL_PRODUCT_NAME,
  FOOTER_LEGAL_RIGHTS_RESERVED,
  FOOTER_LEGAL_VERSION_PREFIX,
} from '@/core/presentation/constants/landing-footer-legal-copy';
import { LogoLink } from '@/features/app/presentation/components/LogoLink';
import {
  LANDING_FOOTER_PWA_FAQ_LINK_LABEL,
  LANDING_FOOTER_PWA_LINE,
} from '@/core/presentation/constants/landing-pwa-promo-copy';
import { useAppContent } from '@/features/app-content/presentation/context/AppContentContext';
import {
  publishedPrivacyContent,
  publishedTermsContent,
} from '@/features/app-content/presentation/utils/published-legal-text';

type LegalModalKind = 'privacy' | 'terms' | null;

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const { state: appContentState } = useAppContent();
  const [legalModal, setLegalModal] = useState<LegalModalKind>(null);

  const openPrivacy = useCallback(() => setLegalModal('privacy'), []);
  const openTerms = useCallback(() => setLegalModal('terms'), []);
  const closeLegal = useCallback(() => setLegalModal(null), []);

  return (
    <footer className="bg-navy-dark border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-6 py-10">
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-center md:justify-between md:gap-6">
          <LogoLink
            className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan/60 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark rounded-md"
            ariaLabel="RetornaAr - Página inicial"
          >
            <AppLogo
              src="/assets/images/retornaArLogo.png"
              responsiveClassName="h-9 sm:h-10 md:h-11"
            />
          </LogoLink>

          <nav
            className="flex w-full max-w-md flex-wrap items-center justify-center gap-x-6 gap-y-3 md:max-w-none md:flex-1 md:justify-end"
            aria-label="Rodapé"
          >
            <a href="#como-funciona" className="text-white/50 hover:text-white/80 text-sm font-medium transition-colors">
              Como Funciona
            </a>
            <a href="#faq" className="text-white/50 hover:text-white/80 text-sm font-medium transition-colors">
              FAQ
            </a>
            <a href="#lista-espera" className="text-white/50 hover:text-white/80 text-sm font-medium transition-colors">
              Lista de Espera
            </a>
            <a href="mailto:contato@retornaAr.com.br" className="text-white/50 hover:text-white/80 text-sm font-medium transition-colors">
              Contato
            </a>
          </nav>

          <div className="flex shrink-0 items-center gap-4 md:pl-2">
            <a
              href="#"
              aria-label="Instagram do RetornaAr"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-cyan hover:border-cyan transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a
              href="#"
              aria-label="WhatsApp do RetornaAr"
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-cyan hover:border-cyan transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </div>
        </div>

        <p className="mt-6 max-w-2xl text-center text-xs leading-relaxed text-white/35 md:text-left">
          {LANDING_FOOTER_PWA_LINE}{' '}
          <a
            href="#faq"
            className="font-medium text-white/45 underline-offset-2 transition-colors hover:text-cyan hover:underline"
          >
            {LANDING_FOOTER_PWA_FAQ_LINK_LABEL}
          </a>
        </p>

        <div className="mt-6 pt-6 border-t border-white/8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-col gap-1 text-center md:text-left">
            <p className="text-xs leading-relaxed text-white/30">
              © {year} {FOOTER_LEGAL_PRODUCT_NAME}. {FOOTER_LEGAL_RIGHTS_RESERVED}{' '}
              {FOOTER_LEGAL_DEVELOPER_CREDIT}
            </p>
            <p className="text-[11px] tabular-nums text-white/20">
              {FOOTER_LEGAL_VERSION_PREFIX} {APP_BUILD_VERSION}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-center gap-6 md:justify-end">
            <button
              type="button"
              onClick={openPrivacy}
              className="text-white/25 hover:text-white/50 text-xs transition-colors underline-offset-2 hover:underline"
            >
              {FOOTER_LEGAL_LINK_PRIVACY}
            </button>
            <button
              type="button"
              onClick={openTerms}
              className="text-white/25 hover:text-white/50 text-xs transition-colors underline-offset-2 hover:underline"
            >
              {FOOTER_LEGAL_LINK_TERMS}
            </button>
          </div>
        </div>
      </div>

      <LegalDocumentViewerModal
        isOpen={legalModal === 'privacy'}
        onClose={closeLegal}
        title={LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_PRIVACY}
        body={publishedPrivacyContent(appContentState.privacy.current)}
        contentInstanceId="privacy"
      />
      <LegalDocumentViewerModal
        isOpen={legalModal === 'terms'}
        onClose={closeLegal}
        title={LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_TERMS}
        body={publishedTermsContent(appContentState.terms.current)}
        contentInstanceId="terms"
      />
    </footer>
  );
};

export default Footer;